import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FamilyRelationshipEditDialogComponent } from '../components/family-relationship-edit-dialog.component';
import { PersonFamilyRelationships, PersonRelationshipItem, PersonService } from '../services/person.service';
import { PersonLookupOption } from '../models/person-event.model';
import { NotificationService } from '@shared/services/notification.service';
import { MemberReferenceComponent } from '@shared/components/member-reference/member-reference.component';
import { getApiErrorMessage } from '@core/http/api-error-message.util';

@Component({ selector: 'app-person-family-relationships-page', standalone: true, imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatInputModule, MatIconModule, MatTooltipModule, MatAutocompleteModule, MatProgressSpinnerModule, MemberReferenceComponent], templateUrl: './person-family-relationships-page.component.html', styleUrl: './person-family-relationships-page.component.scss' })
export class PersonFamilyRelationshipsPageComponent implements OnInit {
  personId = ''; saving = false; isLoadingRelationships = true;
  readonly memberOptions: Record<string, PersonLookupOption[]> = {};
  private readonly searchTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly people = inject(PersonService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly notifications = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  readonly sections = [
    ['parents', 'Padres', 'escalator_warning', 'padres'],
    ['children', 'Hijos', 'child_care', 'hijos'],
  ] as const;
  readonly form = this.fb.group({ parents: this.fb.array([]), children: this.fb.array([]) });
  ngOnInit(): void {
    this.personId = this.route.snapshot.pathFromRoot
      .map(route => route.paramMap.get('person-id'))
      .find((personId): personId is string => !!personId) ?? '';
    this.loadRelationships();
  }
  rows(kind: string): FormArray { return this.form.get(kind) as FormArray; }
  add(kind: string, value?: PersonRelationshipItem): void { this.openDialog(kind, value); }
  edit(kind: string, index: number): void { this.openDialog(kind, this.rows(kind).at(index).getRawValue(), index); }
  remove(kind: string, index: number): void { this.rows(kind).removeAt(index); this.save(); }
  searchKey(kind: string, index: number): string { return `${kind}-${index}`; }
  onMemberSearch(kind: string, index: number, value: string): void {
    const key = this.searchKey(kind, index);
    this.rows(kind).at(index).get('relatedPersonId')?.setValue(null, { emitEvent: false });
    const timer = this.searchTimers.get(key);
    if (timer) clearTimeout(timer);
    const term = value.trim();
    if (term.length < 2) { this.memberOptions[key] = []; return; }
    this.searchTimers.set(key, setTimeout(() => this.people.searchPeople(term).subscribe(options => {
      this.memberOptions[key] = options.filter(option => option.id !== this.personId);
      this.cdr.markForCheck();
    }), 250));
  }
  selectMember(kind: string, index: number, option: PersonLookupOption): void {
    this.rows(kind).at(index).patchValue({ relatedPersonId: option.id, relatedPersonName: option.name });
    this.memberOptions[this.searchKey(kind, index)] = [];
  }
  save(): void { this.saving = true; const map = (kind: 'parents' | 'children', type: PersonRelationshipItem['relationshipType']) => this.rows(kind).getRawValue().map(({ isRelatedPersonDeceased, ...relationship }) => ({ ...relationship, relationshipType: type })); const body: PersonFamilyRelationships = { parents: map('parents', 'Parent'), children: map('children', 'Child') }; this.people.saveFamilyRelationships(this.personId, body).subscribe({ next: () => { this.saving = false; this.refreshRelationships(); }, error: error => { this.saving = false; this.notifications.error(this.getSaveErrorMessage(error)); this.cdr.markForCheck(); } }); }
  back(): void { this.router.navigate(['../'], { relativeTo: this.route }); }
  private populate(data: PersonFamilyRelationships): void { (Object.keys(data) as Array<'parents' | 'children'>).forEach(k => data[k].forEach(v => this.rows(k).push(this.fb.group({ id: [v.id ?? null], relatedPersonId: [v.relatedPersonId ?? null], relatedPersonName: [v.relatedPersonName ?? ''], isRelatedPersonDeceased: [v.isRelatedPersonDeceased ?? false], validFrom: [v.validFrom ?? null], notes: [v.notes ?? ''] })))); }
  private refreshRelationships(): void { (Object.keys(this.form.controls) as Array<'parents' | 'children'>).forEach(key => this.rows(key).clear()); this.loadRelationships(); }
  private getSaveErrorMessage(error: unknown): string {
    const status = typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number'
      ? error.status
      : null;

    return status === 403
      ? 'No tenés autorización para modificar los vínculos familiares de esta persona.'
      : getApiErrorMessage(error, 'No se pudieron guardar los vínculos familiares.');
  }
  private loadRelationships(): void {
    this.isLoadingRelationships = true;
    this.people.getFamilyRelationships(this.personId).subscribe({
      next: data => {
        this.populate(data);
        this.isLoadingRelationships = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingRelationships = false;
        this.notifications.error('No se pudieron cargar los vínculos familiares.');
        this.cdr.markForCheck();
      },
    });
  }
  private openDialog(kind: string, value?: PersonRelationshipItem, index?: number): void { this.dialog.open(FamilyRelationshipEditDialogComponent, { data: { title: value ? 'Editar vínculo' : 'Agregar vínculo', item: value, showValidFrom: false } }).afterClosed().subscribe(result => { if (!result) return; result.validFrom = null; const row = this.fb.group(result); if (index === undefined) this.rows(kind).push(row); else this.rows(kind).at(index).patchValue(result); this.save(); }); }
}
